<script setup lang="ts">
/**
 * Carte d'info riche au survol — coquille standard sur <el-popover>. Normalise
 * l'enveloppe (popper-class `tt-popover` : fond, bord, ombre) et les défauts
 * (hover, délai 200ms, placement haut). Le contenu se construit avec les
 * classes globales `.tt-*` (cf assets/styles/_tooltips.scss).
 *
 * Ergonomie alignée sur <AppTooltip> : le slot PAR DÉFAUT est l'élément
 * déclencheur, le slot #content est la carte affichée.
 *
 *   <AppInfoCard :width="300" placement="bottom">
 *     <ResourceChip ... />            <!-- déclencheur -->
 *     <template #content>
 *       <ResourceTooltipContent ... /> <!-- carte (rend un .tt-card) -->
 *     </template>
 *   </AppInfoCard>
 */
import { computed } from 'vue';
import type { Placement } from 'element-plus';

const props = withDefaults(
  defineProps<{
    placement?: Placement;
    width?: number | string;
    /** Délai avant affichage (ms). */
    showAfter?: number;
    trigger?: 'hover' | 'click' | 'focus' | 'contextmenu' | 'manual';
    disabled?: boolean;
    /** Classe(s) additionnelle(s) sur le popper (fusionnées avec tt-popover). */
    popperClass?: string;
  }>(),
  {
    placement: 'top',
    width: 300,
    showAfter: 200,
    trigger: 'hover',
    disabled: false,
    popperClass: undefined,
  },
);

const mergedPopperClass = computed(() =>
  ['tt-popover', props.popperClass].filter(Boolean).join(' '),
);
</script>

<template>
  <el-popover
    :placement="placement"
    :width="width"
    :show-after="showAfter"
    :trigger="trigger"
    :disabled="disabled"
    :popper-class="mergedPopperClass"
    v-bind="$attrs"
  >
    <template #reference>
      <slot />
    </template>
    <slot name="content" />
  </el-popover>
</template>
